import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../../components/layout/Layout';
import Modal from '../../components/ui/Modal';
import Pagination from '../../components/ui/Pagination';
import api from '../../api/axios';
import toast from 'react-hot-toast';

const DEPARTMENTS = ['Administration', 'Engineering', 'Finance', 'HR', 'IT', 'Marketing', 'Operations', 'Sales', 'Security', 'Other'];
const EMPTY_FORM = { full_name: '', staff_id: '', email: '', phone: '', department: '', password: '' };

export default function StaffManagement() {
  const [staff, setStaff] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [department, setDepartment] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const limit = 15;

  const load = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit });
      if (search) params.append('search', search);
      if (department) params.append('department', department);
      const res = await api.get(`/staff?${params}`);
      setStaff(res.data.staff);
      setTotal(res.data.total);
    } catch {}
    setLoading(false);
  }, [page, search, department]);

  useEffect(() => { load(); }, [load]);

  function openAdd() { setEditTarget(null); setForm(EMPTY_FORM); setShowModal(true); }
  function openEdit(s) { setEditTarget(s); setForm({ full_name: s.full_name, staff_id: s.staff_id, email: s.email, phone: s.phone || '', department: s.department, password: '' }); setShowModal(true); }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editTarget) {
        await api.put(`/staff/${editTarget.id}`, form);
        toast.success('Staff updated');
      } else {
        await api.post('/staff', form);
        toast.success('Staff added successfully');
      }
      setShowModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function toggleStatus(s) {
    try {
      const res = await api.patch(`/staff/${s.id}/toggle-status`);
      toast.success(res.data.message);
      load();
    } catch { toast.error('Failed'); }
  }

  async function handleDelete(s) {
    if (!window.confirm(`Delete ${s.full_name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/staff/${s.id}`);
      toast.success('Staff deleted');
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Delete failed'); }
  }

  return (
    <Layout>
      <div className="page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1>Staff Management</h1>
          <p>Manage all staff accounts</p>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Staff</button>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: '1rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
        <input className="form-input" style={{ flex: 1, minWidth: 200 }} placeholder="Search name, email, staff ID..." value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }} />
        <select className="form-input" style={{ width: 180 }} value={department} onChange={e => { setDepartment(e.target.value); setPage(1); }}>
          <option value="">All Departments</option>
          {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>

      <div className="card">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}><span className="spinner spinner-dark" /></div>
        ) : staff.length === 0 ? (
          <div className="empty-state"><div style={{ fontSize: '3rem' }}>👥</div><p>No staff found</p></div>
        ) : (
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>Name</th><th>Staff ID</th><th>Department</th><th>Email</th><th>Status</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {staff.map(s => (
                    <tr key={s.id}>
                      <td style={{ fontWeight: 500 }}>{s.full_name}</td>
                      <td style={{ color: '#64748b' }}>{s.staff_id}</td>
                      <td>{s.department}</td>
                      <td style={{ color: '#64748b', fontSize: '0.85rem' }}>{s.email}</td>
                      <td><span className={`badge badge-${s.is_active ? 'active' : 'inactive'}`}>{s.is_active ? 'Active' : 'Disabled'}</span></td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.375rem' }}>
                          <button className="btn btn-sm btn-secondary" onClick={() => openEdit(s)}>Edit</button>
                          <button className="btn btn-sm" style={{ background: s.is_active ? '#fef3c7' : '#dcfce7', color: s.is_active ? '#92400e' : '#166534' }} onClick={() => toggleStatus(s)}>
                            {s.is_active ? 'Disable' : 'Enable'}
                          </button>
                          <button className="btn btn-sm btn-danger" onClick={() => handleDelete(s)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} total={total} limit={limit} onPageChange={setPage} />
          </>
        )}
      </div>

      {showModal && (
        <Modal title={editTarget ? 'Edit Staff' : 'Add New Staff'} onClose={() => setShowModal(false)}>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Full Name *</label>
                <input className="form-input" required value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Staff ID *</label>
                <input className="form-input" required value={form.staff_id} onChange={e => setForm({ ...form, staff_id: e.target.value })} disabled={!!editTarget} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Email *</label>
                <input className="form-input" type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input className="form-input" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Department *</label>
              <select className="form-input" required value={form.department} onChange={e => setForm({ ...form, department: e.target.value })}>
                <option value="">Select department</option>
                {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            {!editTarget && (
              <div className="form-group">
                <label className="form-label">Password (default: Password@123)</label>
                <input className="form-input" type="password" placeholder="Leave blank for default" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
              <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? <><span className="spinner" /> Saving...</> : 'Save'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </Layout>
  );
}
